import { useNavigate } from 'react-router-dom';

import { Box, Button, Typography } from '@mui/material';

import CustomBackdrop from '../../../components/CustomBackdrop/CustomBackdrop';
import { useFinishCrawlResultValidationMutation } from '../../../gql/generated/graphql';
import { useSnackbar } from '../../../providers/SnackbarProvider';
import { paths } from '../../../routes/paths';

interface Props {
  crawlResultId: number;
  userId: number | null;
  authId: string | null;
  onReleaseLock: () => Promise<void>;
}

const CrawlResultFinishStep = ({ crawlResultId, userId, authId, onReleaseLock }: Props) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [finishCrawlValidation, { loading }] = useFinishCrawlResultValidationMutation();

  const handleSubmit = async () => {
    if (!userId || !authId) return;
    const result = await finishCrawlValidation({
      variables: { crawlResultId, userId, authId },
    });

    const data = result.data?.finishCrawlResultValidation;
    if (data?.success) {
      const batchCount = data.batchValidatedCount || 1;
      const message =
        batchCount > 1
          ? `Crawl validation complete (${batchCount} crawl results validated)`
          : 'Crawl validation complete';
      showSnackbar(message, 'success');
      await onReleaseLock();
      navigate(paths.crawlerInbox);
    } else {
      showSnackbar(data?.message || 'Failed to complete crawl validation', 'error');
    }
  };

  return (
    <Box>
      <Typography variant="body1" gutterBottom>
        You have reviewed the crawl result data. Click below to complete the crawl result validation.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        This will mark the crawl result as validated and release your lock on this company.
      </Typography>
      <Box textAlign="center">
        <Button variant="contained" onClick={handleSubmit} disabled={loading || !userId || !authId}>
          Complete Crawl Validation
        </Button>
      </Box>
      <CustomBackdrop open={loading} />
    </Box>
  );
};

export default CrawlResultFinishStep;
